// ============================================
// Streaming Reactive Chat-Pool
// ============================================
// Loads pinked-web/assets/data/streaming-chat-lines.json and exposes a
// state-aware picker. Replaces the static "{get_viewer_name()}: '...'"
// flavor pattern in Ink for category-specific reactive chatter.
//
// Plan: STREAMING_REWORK.md § Chat Rework Phase A.
// Schema doc: streaming-chat-lines.json _doc field.
//
// Picker pipeline (per pick(state)):
//   1. Filter by category match (line.category includes state.category OR "generic")
//   2. Filter by tags_required (every entry must be present in state.tags)
//   3. Filter by tags_forbidden (no entry may be present in state.tags)
//   4. Filter by hype range (state.hype within [hype_min, hype_max])
//   5. Filter out the last 5 picked IDs (skip-buffer, no-repeat-too-soon)
//   6. Weighted random over remaining
//
// State shape produced by buildState() at runtime:
//   {
//     category: 'cute' | 'naughty' | 'domme' | 'fetish' | 'cosplay' | 'public_risk',
//     hype: number,                  // current s_hype
//     tags: Set<string>              // computed tags (cage_visible, outfit_*, etc.)
//   }

import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';

const POOL_URL = 'assets/data/streaming-chat-lines.json';

// Map s_category integer (0-5) to category string used in the pool.
const CATEGORY_NAMES = ['cute', 'naughty', 'domme', 'fetish', 'cosplay', 'public_risk'];

// Anonymous-viewer pool — picker substitutes {viewer} with one of these,
// matches the existing get_viewer_name() flavor in Ink. Lowercase, varied.
const VIEWER_NAMES = [
    'anon42', 'sissywatch_99', 'lurking_jen', 'pink_purr', 'velvet_v',
    'newhere_x', 'long_time_listener', 'tip_then_lurk', 'mod_in_training',
    'beelzebabe', 'lateagain', 'just_passing', 'sub_to_sub', 'phonebanker',
    'noctourist', 'foundyou', 'rentmoney', 'goth_ghost', 'whisper_anon',
    'paying_attention',
];

class StreamingChatPool {
    constructor() {
        this._lines = [];
        this._loaded = false;
        this._loadPromise = null;
        this._recentIds = [];   // skip-buffer, last 5 picked IDs
    }

    /**
     * Load the JSON pool. Idempotent — multiple calls share one fetch.
     * @returns {Promise<void>}
     */
    async load() {
        if (this._loaded) return;
        if (this._loadPromise) return this._loadPromise;
        this._loadPromise = (async () => {
            try {
                const res = await fetch(POOL_URL);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                this._lines = Array.isArray(data.lines) ? data.lines : [];
                this._loaded = true;
            } catch (e) {
                console.warn('[ChatPool] failed to load pool:', e?.message || e);
                this._lines = [];
                this._loaded = true;  // still mark loaded so we don't retry on every pick
            }
        })();
        return this._loadPromise;
    }

    /**
     * Direct loader for Node tests — bypasses fetch, takes a JSON object.
     * @param {{lines: Array}} data
     */
    loadDirect(data) {
        this._lines = Array.isArray(data?.lines) ? data.lines : [];
        this._loaded = true;
    }

    /**
     * Build the runtime state object from variableManager + story state.
     * @returns {{category: string, hype: number, tags: Set<string>}}
     */
    buildState() {
        const tags = new Set();

        const story = storyManager?.currentStory;
        const get = (name) => {
            try { return story ? story.variablesState[name] : null; }
            catch (e) { return null; }
        };

        const categoryIdx = get('s_category') || 0;
        const category = CATEGORY_NAMES[categoryIdx] || 'cute';

        // Boolean state → tag flags. Coerce inkjs booleans (0/1 or true/false).
        if (get('wearing_chastity')) tags.add('cage_visible');
        if (get('set_neko'))         tags.add('outfit_neko');
        if (get('set_bunny_girl'))   tags.add('outfit_bunny');
        if (get('set_dark_doll'))    tags.add('outfit_dark_doll');
        if (get('set_full_lingerie')) tags.add('outfit_lingerie');
        if (get('set_maid_service')) tags.add('outfit_maid');
        if (get('s_shy_mode_active')) tags.add('shy_mode');

        // Stream-experience tier
        const streamsCompleted = get('streams_completed') || 0;
        if (streamsCompleted < 5)  tags.add('early_stream');
        if (streamsCompleted > 30) tags.add('experienced');

        // MASC_ROUTE audit r3 (2026-06-07): a committed masc streamer must not
        // be chatted at as she/her/girl. The tag drives a regex screen in
        // _matches so the whole JSON pool is covered without per-line edits.
        if ((Number(get('masc_path')) || 0) >= 1 && get('presenting_masc')) tags.add('pc_masc');

        const hype = Number(get('s_hype')) || 0;

        return { category, hype, tags };
    }

    /**
     * Score and pick one chat line for the given state.
     * @param {{category: string, hype: number, tags: Set<string>}} state
     * @returns {{id: string, text: string} | null}
     */
    pick(state) {
        if (!this._loaded || this._lines.length === 0) return null;

        const candidates = this._lines.filter(line => this._matches(line, state));
        if (candidates.length === 0) return null;

        // Weighted random
        const total = candidates.reduce((s, l) => s + (l.weight || 1.0), 0);
        let roll = Math.random() * total;
        let chosen = candidates[0];
        for (const line of candidates) {
            roll -= (line.weight || 1.0);
            if (roll <= 0) { chosen = line; break; }
        }

        this._noteRecent(chosen.id);
        return {
            id: chosen.id,
            text: this._renderText(chosen.text),
        };
    }

    /**
     * Pick N distinct chat lines for the given state. Best-effort: if the
     * pool can't supply N unique, returns however many it could.
     * @param {object} state
     * @param {number} n
     */
    pickN(state, n) {
        const picks = [];
        const usedIds = new Set();
        for (let i = 0; i < n; i++) {
            // Temporarily extend skip-buffer to include this pickN's prior picks
            const tmpRecent = [...this._recentIds, ...usedIds];
            const candidates = this._lines.filter(line =>
                !tmpRecent.includes(line.id) && this._matches(line, state)
            );
            if (candidates.length === 0) break;

            const total = candidates.reduce((s, l) => s + (l.weight || 1.0), 0);
            let roll = Math.random() * total;
            let chosen = candidates[0];
            for (const line of candidates) {
                roll -= (line.weight || 1.0);
                if (roll <= 0) { chosen = line; break; }
            }

            picks.push({ id: chosen.id, text: this._renderText(chosen.text) });
            usedIds.add(chosen.id);
            this._noteRecent(chosen.id);
        }
        return picks;
    }

    // ── Internal ────────────────────────────────────────────────────────

    _matches(line, state) {
        // 0. MASC_ROUTE audit r3: masc-presenting route PC never receives
        // she/her/girl-coded chat. Lines explicitly authored for him
        // (tags_required includes 'pc_masc') bypass the regex screen.
        if (state.tags.has('pc_masc')
            && !(line.tags_required && line.tags_required.includes('pc_masc'))
            && /\b(she|her|hers|girl|girly|gurl|princess|queen|goddess|ma'am|sissy)\b/i.test(line.text || '')) {
            return false;
        }
        // 1. Category match: 'generic' matches any, otherwise must include state.category
        if (!line.category.includes('generic') && !line.category.includes(state.category)) {
            return false;
        }
        // 2. tags_required: ALL must be in state.tags
        if (line.tags_required && line.tags_required.length > 0) {
            for (const t of line.tags_required) {
                if (!state.tags.has(t)) return false;
            }
        }
        // 3. tags_forbidden: NONE may be in state.tags
        if (line.tags_forbidden && line.tags_forbidden.length > 0) {
            for (const t of line.tags_forbidden) {
                if (state.tags.has(t)) return false;
            }
        }
        // 4. Hype range
        const min = (typeof line.hype_min === 'number') ? line.hype_min : 0;
        const max = (typeof line.hype_max === 'number') ? line.hype_max : 100;
        if (state.hype < min || state.hype > max) return false;
        // 5. Skip-buffer
        if (this._recentIds.includes(line.id)) return false;
        return true;
    }

    _renderText(template) {
        // {viewer} → random anonymous handle
        return template.replace(/\{viewer\}/g, () => {
            return VIEWER_NAMES[Math.floor(Math.random() * VIEWER_NAMES.length)];
        });
    }

    _noteRecent(id) {
        this._recentIds.push(id);
        if (this._recentIds.length > 5) this._recentIds.shift();
    }

    /**
     * Reset the skip-buffer. Called on stream start so a fresh stream's first
     * pick isn't blocked by the previous stream's tail.
     */
    resetRecent() {
        this._recentIds = [];
    }

    // Test helpers
    _getLines() { return this._lines; }
    _getRecentIds() { return [...this._recentIds]; }
}

export const streamingChatPool = new StreamingChatPool();
